import React from "react";

type ActivateHandler = (
  e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
) => void;

interface PressableOptions {
  /** Renders the element as disabled (removes it from the tab order). */
  disabled?: boolean;
  /** Accessible name when the visible content is an icon or ambiguous. */
  label?: string;
  /** ARIA role to expose. Defaults to "button". */
  role?: React.AriaRole;
  /** `aria-expanded` for elements that toggle a popup/menu. */
  expanded?: boolean;
  /** `aria-haspopup` for elements that open a menu/dialog/listbox. */
  hasPopup?: React.AriaAttributes["aria-haspopup"];
  /** `aria-current` (e.g. "step" for a stepper pill). */
  current?: React.AriaAttributes["aria-current"];
  /** `aria-pressed` / `aria-selected` state for toggles and tabs. */
  selected?: boolean;
  /** When set, `selected` maps to `aria-selected` (tabs) instead of `aria-pressed`. */
  selectedAttr?: "aria-pressed" | "aria-selected" | "aria-checked";
}

/**
 * Returns the props needed to make a non-semantic clickable element
 * (`Box` / `Typography`) behave like a native button for keyboard and
 * assistive-technology users — focusable, role-exposed and activated by
 * Enter/Space — without changing its visual box model.
 *
 * Prefer a real MUI `Button` / `IconButton` where the design allows; use this
 * only for the mobile flow's custom-styled clickable surfaces that cannot use a
 * button element without altering the layout.
 */
export const pressableProps = (
  onActivate?: ActivateHandler,
  options: PressableOptions = {},
) => {
  const {
    disabled,
    label,
    role = "button",
    expanded,
    hasPopup,
    current,
    selected,
    selectedAttr = "aria-pressed",
  } = options;

  const selectedState: {
    "aria-pressed"?: boolean;
    "aria-selected"?: boolean;
    "aria-checked"?: boolean;
  } = {};
  if (selected !== undefined) {
    selectedState[selectedAttr] = selected;
  }

  return {
    role,
    tabIndex: disabled ? -1 : 0,
    "aria-label": label,
    "aria-disabled": disabled || undefined,
    "aria-expanded": expanded,
    "aria-haspopup": hasPopup,
    "aria-current": current,
    ...selectedState,
    onClick: disabled ? undefined : onActivate,
    onKeyDown: disabled
      ? undefined
      : (e: React.KeyboardEvent<HTMLElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onActivate?.(e);
          }
        },
  };
};
